import { BaseTariffPage } from "@/components/tarif/BaseTariffPage";
import { Shield, AlertCircle, CheckCircle2, Clock, Phone, FileText, Stethoscope, Users, Building2, Hospital, Lock, Globe, Zap, HeartPulse } from "lucide-react";

const COGITANDA_COLOR = "#0066B3";

const CogitandaTarif = () => {
  const heroFeatures = [
    { icon: Phone, title: "24/7 Notfall-Hotline", subtitle: "Immer erreichbar" },
    { icon: Shield, title: "Umfassender Schutz", subtitle: "Alle Cyber-Risiken" },
    { icon: Globe, title: "Risiko-Check", subtitle: "IT-Sicherheitsanalyse" }
  ];

  const coverageAreas = [
    {
      icon: Zap,
      title: "Cyber-Notfallmanagement 24/7",
      items: [
        "Sofortige Incident Response bei Cyber-Angriffen",
        "IT-Forensik und Schadenanalyse",
        "Krisenmanagement und Kommunikationsberatung",
        "Koordination von Spezialisten",
        "Wiederherstellung der IT-Systeme"
      ]
    },
    {
      icon: Clock,
      title: "Betriebsunterbrechung",
      items: [
        "Ersatz für entgangenen Gewinn",
        "Deckung fortlaufender Betriebskosten",
        "Mehrkosten für Notfallbetrieb",
        "Unterbrechung durch Dienstleister",
        "Keine Wartezeit bei Schadenseintritt"
      ]
    },
    {
      icon: FileText,
      title: "Datenrettung & Wiederherstellung",
      items: [
        "Professionelle Datenwiederherstellung",
        "Forensische Datenanalyse",
        "Systemwiederaufbau und Recovery",
        "Backup-System-Wiederherstellung",
        "Kosten für IT-Dienstleister"
      ]
    },
    {
      icon: Lock,
      title: "Cyber-Erpressung & Haftpflicht",
      items: [
        "Spezialistenservice bei Ransomware",
        "Lösegeldzahlungen und Verhandlungen",
        "DSGVO-Bußgelder und Schadensersatz",
        "Rechtskosten und Anwaltsgebühren",
        "PR-Krisenmanagement"
      ]
    }
  ];

  const caseStudies = [
    {
      title: "Ransomware-Angriff auf Zahnarztpraxis",
      description: "Eine Zahnarztpraxis wurde Opfer eines Ransomware-Angriffs. Alle Patientendaten wurden verschlüsselt und die Praxis musste für mehrere Tage schließen. Die Hacker forderten ein hohes Lösegeld für die Freigabe der Daten.",
      outcome: "Die Cyberversicherung übernahm die Kosten für IT-Forensik, Datenwiederherstellung, Lösegeldzahlungen sowie den Ertragsausfall während der Schließung.",
      damage: "~45.000 €"
    },
    {
      title: "Datenleck bei Hausarztpraxis",
      description: "Durch einen Hackerangriff gelangten vertrauliche Patientendaten einer Hausarztpraxis ins Internet. Mehrere Patienten stellten Schadensersatzforderungen wegen Verletzung ihrer Persönlichkeitsrechte und DSGVO-Verstößen.",
      outcome: "Die Cyber-Haftpflicht übernahm alle Schadensersatzforderungen, Anwaltskosten sowie die DSGVO-Bußgelder und PR-Beratung.",
      damage: "~28.000 €"
    },
    {
      title: "Phishing-Attacke auf MVZ",
      description: "Ein Mitarbeiter eines Medizinischen Versorgungszentrums fiel auf eine Phishing-E-Mail herein und gab Zugangsdaten preis. Angreifer verschafften sich Zugriff auf das Abrechnungssystem und manipulierten Zahlungsdaten.",
      outcome: "Die Versicherung deckte die IT-Sicherheitsanalyse, Systembereinigung, Mitarbeiterschulungen sowie entstandene Vermögensschäden.",
      damage: "~22.000 €"
    },
    {
      title: "DDoS-Angriff auf Klinik-Website",
      description: "Die Online-Terminvergabe einer Klinik wurde durch einen DDoS-Angriff für mehrere Tage lahmgelegt. Patienten konnten keine Termine buchen, was zu erheblichen Umsatzeinbußen führte.",
      outcome: "Die Betriebsunterbrechungsversicherung ersetzte den Ertragsausfall und die Kosten für IT-Sicherheitsmaßnahmen zur Abwehr weiterer Angriffe.",
      damage: "~35.000 €"
    }
  ];

  const targetGroups = [
    {
      icon: Stethoscope,
      title: "Arztpraxen",
      description: "Hausärzte, Fachärzte und Gemeinschaftspraxen mit digitalen Patientendaten",
      benefits: [
        "24/7-Notfall-Hotline für Cyber-Vorfälle",
        "Schutz sensibler Patientendaten",
        "DSGVO-konforme Schadensregulierung",
        "Praxis-IT-Sicherheitsanalyse inklusive"
      ],
      recommendation: "Empfohlene Deckung: 100.000 - 250.000 €"
    },
    {
      icon: HeartPulse,
      title: "MVZ & Kliniken",
      description: "Medizinische Versorgungszentren und Kliniken mit komplexer IT-Infrastruktur",
      benefits: [
        "Schutz vernetzter Medizintechnik",
        "Betriebsunterbrechungsschutz",
        "Forensische Schadensanalyse",
        "Krisenmanagement und PR-Beratung"
      ],
      recommendation: "Empfohlene Deckung: 250.000 - 1.000.000 €"
    },
    {
      icon: Users,
      title: "Therapiepraxen",
      description: "Physiotherapie, Ergotherapie, Logopädie und psychotherapeutische Praxen",
      benefits: [
        "Schutz digitaler Behandlungspläne",
        "Präventive Sicherheitsschulungen",
        "Günstige Prämien für kleine Praxen",
        "Schnelle Schadensregulierung"
      ],
      recommendation: "Empfohlene Deckung: 50.000 - 100.000 €"
    },
    {
      icon: Building2,
      title: "Medizinische Dienstleister",
      description: "Labore, Apotheken und medizinische Serviceunternehmen",
      benefits: [
        "Schutz von Analysedaten und Rezepturen",
        "Absicherung digitaler Lieferketten",
        "Flexible Versicherungssummen",
        "Individuelle Risikoanalyse"
      ],
      recommendation: "Empfohlene Deckung: 100.000 - 500.000 €"
    }
  ];

  const insuranceConditions = [
    {
      icon: Shield,
      title: "Versicherungssummen",
      description: "Flexibel wählbar von 50.000 EUR bis 5 Mio. EUR je nach Unternehmensgröße und Risikoprofil"
    },
    {
      icon: FileText,
      title: "Selbstbeteiligung",
      description: "Wahlweise ohne oder mit Selbstbeteiligung - für optimale Prämiengestaltung"
    },
    {
      icon: CheckCircle2,
      title: "Sofortschutz",
      description: "Keine Wartezeiten - Versicherungsschutz beginnt sofort nach Vertragsabschluss"
    },
    {
      icon: Clock,
      title: "Vertragslaufzeit",
      description: "Flexible Laufzeiten mit 3 Monaten Kündigungsfrist zum Hauptfälligkeitstermin"
    },
    {
      icon: Globe,
      title: "Präventionsleistungen",
      description: "IT-Sicherheitsanalyse, Mitarbeiterschulungen und Awareness-Trainings inklusive"
    },
    {
      icon: Users,
      title: "Weltweiter Schutz",
      description: "Versicherungsschutz gilt weltweit für alle Geschäftstätigkeiten des Unternehmens"
    }
  ];

  const faqs = [
    {
      question: "Was leistet die Cogitanda Cyberversicherung?",
      answer: "Die Cogitanda Cyberversicherung bietet umfassenden Schutz bei Cyber-Angriffen: 24/7-Notfallmanagement, Datenwiederherstellung, Betriebsunterbrechung, Cyber-Erpressung, DSGVO-Bußgelder und Cyber-Haftpflicht. Zusätzlich sind Präventionsleistungen wie IT-Sicherheitsanalysen und Mitarbeiterschulungen enthalten."
    },
    {
      question: "Für wen eignet sich die Cogitanda Cyberversicherung?",
      answer: "Die Versicherung eignet sich für alle Unternehmen und Praxen im Gesundheitswesen: Arztpraxen, MVZ, Kliniken, Therapiepraxen, Apotheken und medizinische Dienstleister. Besonders wichtig ist der Schutz für Betriebe, die sensible Patientendaten verarbeiten."
    },
    {
      question: "Wie hoch sollte die Versicherungssumme sein?",
      answer: "Die Versicherungssumme richtet sich nach Unternehmensgröße und Risikoprofil. Kleine Praxen wählen meist 50.000-100.000 EUR, mittlere Praxen 100.000-250.000 EUR, größere Einrichtungen wie MVZ und Kliniken 250.000-1.000.000 EUR oder mehr."
    },
    {
      question: "Gibt es Wartezeiten bei der Cogitanda Cyberversicherung?",
      answer: "Nein, es gibt keine Wartezeiten. Der Versicherungsschutz beginnt sofort nach Vertragsabschluss. Die 24/7-Notfall-Hotline steht ab dem ersten Tag zur Verfügung."
    },
    {
      question: "Was kostet die Cogitanda Cyberversicherung?",
      answer: "Die Beitragshöhe hängt von mehreren Faktoren ab: Branche, Unternehmensgröße, Umsatz, IT-Sicherheitsmaßnahmen und gewählte Versicherungssumme. Für eine individuelle Berechnung kontaktieren Sie uns gerne für ein persönliches Angebot."
    },
    {
      question: "Sind Präventionsleistungen enthalten?",
      answer: "Ja, die Cogitanda Cyberversicherung enthält umfangreiche Präventionsleistungen: IT-Sicherheitsanalyse, Schwachstellenscans, Mitarbeiterschulungen, Awareness-Trainings und Phishing-Simulationen zur Vermeidung von Cyber-Schäden."
    },
    {
      question: "Wie funktioniert die 24/7-Notfall-Hotline?",
      answer: "Bei einem Cyber-Vorfall steht die Notfall-Hotline rund um die Uhr zur Verfügung. Sie erhalten sofort Zugang zu IT-Spezialisten, Forensikern und Krisenmanagern, die den Schaden analysieren und die Wiederherstellung koordinieren."
    },
    {
      question: "Sind auch DSGVO-Bußgelder versichert?",
      answer: "Ja, die Cogitanda Cyberversicherung deckt DSGVO-Bußgelder ab, soweit diese versicherbar sind. Ebenso sind Schadensersatzforderungen Betroffener und Kosten für die Meldung bei Aufsichtsbehörden enthalten."
    }
  ];

  const documents = [
    {
      title: "Produktinformationsblatt Cyberversicherung",
      type: "PDF",
      url: "https://www.cogitanda.com/downloads/pib-cyber.pdf"
    },
    {
      title: "Versicherungsbedingungen (AVB)",
      type: "PDF",
      url: "https://www.cogitanda.com/downloads/avb-cyber.pdf"
    }
  ];

  return (
    <BaseTariffPage
      companyName="Cogitanda"
      companyColor={COGITANDA_COLOR}
      productRating={{ value: 4.5, count: 156 }}
      priceRange="70-250"
      pageTitle="Cogitanda Cyberversicherung - Umfassender Schutz für Gesundheitswesen"
      metaDescription="Die Cogitanda Cyberversicherung bietet Arztpraxen, MVZ und Kliniken umfassenden Schutz vor Cyber-Risiken mit 24/7-Notfall-Hotline und Präventionsleistungen."
      metaKeywords="Cogitanda Cyberversicherung, Cyber-Versicherung Gesundheitswesen, Arztpraxis Cyberschutz, MVZ Cyber-Versicherung"
      canonicalUrl="https://praxis-versichert.de/tarifwissen/cogitanda"
      heroLabel="Spezialisiert auf das Gesundheitswesen"
      heroTitle="Cogitanda Cyberversicherung für medizinische Einrichtungen"
      heroDescription="Umfassender Schutz vor Cyber-Risiken mit 24/7-Notfallmanagement, IT-Sicherheitsanalyse und Präventionsleistungen. Speziell entwickelt für Arztpraxen, MVZ, Kliniken und medizinische Dienstleister."
      heroFeatures={heroFeatures}
      alertTitle="Warum Cogitanda Cyberversicherung?"
      alertDescription="Cyber-Angriffe auf das Gesundheitswesen nehmen dramatisch zu. Die durchschnittliche Ausfallzeit nach einem Angriff beträgt 21 Tage. Die Cogitanda Cyberversicherung schützt Sie mit sofortiger 24/7-Hilfe und umfassenden Präventionsmaßnahmen."
      coverageAreas={coverageAreas}
      targetGroups={targetGroups}
      insuranceConditions={insuranceConditions}
      faqs={faqs}
      documents={documents}
    />
  );
};

export default CogitandaTarif;
